import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import AddressMapPicker from '../../components/AddressMapPicker';
import useLocation from '../../hooks/useLocation';

function Profile() {
  const { user } = useAuth();
  const { getAddressFromCurrentLocation, loading: locationLoading } = useLocation();
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    coordinates: {
      lat: null,
      lng: null
    },
    isDefault: false
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await userAPI.getAddresses();
      setAddresses(response.data.data.addresses);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAddress) {
        await userAPI.updateAddress(editingAddress._id, formData);
        alert('Address updated successfully!');
      } else {
        await userAPI.addAddress(formData);
        alert('Address added successfully!');
      }
      fetchAddresses();
      resetForm();
    } catch (error) {
      alert('Failed to save address: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label,
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      coordinates: address.coordinates || { lat: null, lng: null },
      isDefault: address.isDefault
    });
    setShowAddressForm(true);
  };

  const handleDelete = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await userAPI.deleteAddress(addressId);
        alert('Address deleted successfully!');
        fetchAddresses();
      } catch (error) {
        alert('Failed to delete address');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      coordinates: {
        lat: null,
        lng: null
      },
      isDefault: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };

  const handleDetectLocation = async () => {
    try {
      // First, get GPS coordinates (this always works if permission granted)
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      // Try to get address details (but don't fail if this doesn't work)
      let addressDetails = null;
      try {
        addressDetails = await getAddressFromCurrentLocation();
      } catch (addressError) {
        console.warn('Could not get address details, but GPS coordinates were obtained:', addressError);
      }

      // Update form with GPS coordinates and address if available
      setFormData(prev => ({
        ...prev,
        ...(addressDetails && {
          street: addressDetails.street || prev.street,
          city: addressDetails.city || prev.city,
          state: addressDetails.state || prev.state,
          zipCode: addressDetails.zipCode || prev.zipCode,
        }),
        coordinates: {
          lat: lat,
          lng: lng
        }
      }));

      if (addressDetails) {
        alert('✅ Location and GPS coordinates detected successfully!');
      } else {
        alert('✅ GPS coordinates detected successfully!\n\n⚠️ Note: Could not auto-detect address details. Please enter your street address, city, state, and ZIP code manually.');
      }
    } catch (error) {
      let errorMessage = 'Failed to detect location. ';
      if (error.code === 1) {
        errorMessage += 'Please enable location permissions in your browser settings.';
      } else if (error.code === 2) {
        errorMessage += 'Location information is unavailable.';
      } else if (error.code === 3) {
        errorMessage += 'Location request timed out.';
      } else {
        errorMessage += error.message || 'Please ensure location permissions are enabled.';
      }
      alert(errorMessage);
    }
  };

  const handleMapLocationSelect = (locationData) => {
    setFormData(prev => ({
      ...prev,
      street: locationData.street || prev.street,
      city: locationData.city || prev.city,
      state: locationData.state || prev.state,
      zipCode: locationData.zipCode || prev.zipCode,
      coordinates: locationData.coordinates
    }));
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            👤 My Profile
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Manage your personal information and addresses
          </p>
        </div>

        {/* User Info Card */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-2xl)',
          background: 'var(--gradient-primary)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              backdropFilter: 'blur(10px)'
            }}>
              👤
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: 'var(--font-size-3xl)',
                fontWeight: '800',
                marginBottom: 'var(--space-md)',
                color: 'white'
              }}>
                {user?.name}
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
                <div style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <span>📧</span>
                  <span style={{ fontWeight: '600' }}>{user?.email}</span>
                </div>
                <div style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <span>📱</span>
                  <span style={{ fontWeight: '600' }}>{user?.phone || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Addresses Section */}
        <div className="card fade-in">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-xl)',
            flexWrap: 'wrap',
            gap: 'var(--space-md)'
          }}>
            <h2 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '700',
              color: 'var(--gray-900)',
              margin: 0
            }}>
              📍 My Addresses
            </h2>
            <button
              onClick={() => setShowAddressForm(!showAddressForm)}
              className="btn btn-primary"
            >
              {showAddressForm ? '✖️ Cancel' : '➕ Add New Address'}
            </button>
          </div>

          {/* Address Form */}
          {showAddressForm && (
            <form onSubmit={handleSubmit} className="fade-in" style={{
              marginBottom: 'var(--space-2xl)',
              padding: 'var(--space-xl)',
              background: 'var(--gray-100)',
              borderRadius: 'var(--radius-lg)',
              border: '2px solid var(--gray-200)'
            }}>
              <h3 style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'var(--gray-900)'
              }}>
                {editingAddress ? '✏️ Edit Address' : '➕ Add New Address'}
              </h3>

              <div className="form-group">
                <label>🏷️ Label</label>
                <input
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  placeholder="Home, Office, etc."
                  required
                />
              </div>

              {/* Location Detection Button */}
              <button
                type="button"
                onClick={handleDetectLocation}
                disabled={locationLoading}
                className="btn btn-secondary"
                style={{
                  width: '100%',
                  marginBottom: 'var(--space-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-sm)',
                  background: locationLoading ? 'var(--gray-400)' : 'var(--gradient-secondary)'
                }}
              >
                <span>{locationLoading ? '🔄' : '📍'}</span>
                <span>{locationLoading ? 'Detecting Location...' : 'Use Current Location'}</span>
              </button>

              {/* Map Picker */}
              <AddressMapPicker
                initialPosition={formData.coordinates.lat && formData.coordinates.lng ? formData.coordinates : null}
                onLocationSelect={handleMapLocationSelect}
              />

              <div className="form-group">
                <label>🏠 Street Address</label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="123 Main Street, Apt 4B"
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                <div className="form-group">
                  <label>🏙️ City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🗺️ State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>📮 ZIP Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="10001"
                  required
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleInputChange}
                  />
                  <span>⭐ Set as default address</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
                <button type="submit" className="btn btn-success btn-lg" style={{ flex: 1 }}>
                  {editingAddress ? '💾 Update Address' : '✅ Save Address'}
                </button>
                <button type="button" onClick={resetForm} className="btn btn-secondary btn-lg" style={{ flex: 1 }}>
                  ✖️ Cancel
                </button>
              </div>
            </form>
          )}

          {/* Address List */}
          {addresses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>📍</div>
              <h3 style={{ color: 'var(--gray-700)', marginBottom: 'var(--space-md)' }}>
                No addresses saved yet
              </h3>
              <p style={{ color: 'var(--gray-600)' }}>
                Click "Add New Address" to get started
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-lg)' }}>
              {addresses.map((address) => (
                <div
                  key={address._id}
                  className="fade-in"
                  style={{
                    padding: 'var(--space-xl)',
                    borderRadius: 'var(--radius-lg)',
                    background: address.isDefault ? 'rgba(255, 107, 107, 0.05)' : 'white',
                    border: address.isDefault ? '3px solid var(--primary)' : '2px solid var(--gray-200)',
                    position: 'relative',
                    transition: 'all var(--transition-base)',
                    overflow: 'hidden'
                  }}
                >
                  {address.isDefault && (
                    <div style={{
                      position: 'absolute',
                      top: 'var(--space-lg)',
                      right: 'var(--space-lg)',
                      padding: 'var(--space-xs) var(--space-md)',
                      background: 'var(--gradient-primary)',
                      color: 'white',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: '700',
                      boxShadow: 'var(--shadow-sm)'
                    }}>
                      ⭐ DEFAULT
                    </div>
                  )}

                  <div style={{ paddingRight: '100px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-md)',
                      marginBottom: 'var(--space-lg)'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'var(--gradient-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px'
                      }}>
                        📍
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: 'var(--font-size-2xl)',
                          fontWeight: '700',
                          color: 'var(--gray-900)',
                          marginBottom: 'var(--space-xs)'
                        }}>
                          {address.label}
                        </h3>
                        <p style={{
                          margin: 0,
                          color: 'var(--gray-600)',
                          fontSize: 'var(--font-size-base)'
                        }}>
                          {address.street}
                        </p>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--space-md)',
                      marginBottom: 'var(--space-lg)',
                      paddingTop: 'var(--space-md)',
                      borderTop: '2px solid var(--gray-100)'
                    }}>
                      <div style={{
                        padding: 'var(--space-sm) var(--space-lg)',
                        background: 'rgba(78, 205, 196, 0.1)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid rgba(78, 205, 196, 0.2)'
                      }}>
                        <p style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-600)',
                          marginBottom: 'var(--space-xs)',
                          fontWeight: '600'
                        }}>
                          CITY
                        </p>
                        <p style={{
                          fontSize: 'var(--font-size-base)',
                          fontWeight: '700',
                          color: 'var(--secondary)',
                          margin: 0
                        }}>
                          {address.city}
                        </p>
                      </div>
                      <div style={{
                        padding: 'var(--space-sm) var(--space-lg)',
                        background: 'rgba(255, 107, 107, 0.1)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid rgba(255, 107, 107, 0.2)'
                      }}>
                        <p style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-600)',
                          marginBottom: 'var(--space-xs)',
                          fontWeight: '600'
                        }}>
                          STATE
                        </p>
                        <p style={{
                          fontSize: 'var(--font-size-base)',
                          fontWeight: '700',
                          color: 'var(--primary)',
                          margin: 0
                        }}>
                          {address.state}
                        </p>
                      </div>
                      <div style={{
                        padding: 'var(--space-sm) var(--space-lg)',
                        background: 'rgba(255, 184, 77, 0.1)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid rgba(255, 184, 77, 0.2)'
                      }}>
                        <p style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-600)',
                          marginBottom: 'var(--space-xs)',
                          fontWeight: '600'
                        }}>
                          ZIP CODE
                        </p>
                        <p style={{
                          fontSize: 'var(--font-size-base)',
                          fontWeight: '700',
                          color: 'var(--warning)',
                          margin: 0
                        }}>
                          {address.zipCode}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
                      <button
                        onClick={() => handleEdit(address)}
                        className="btn btn-secondary"
                        style={{ flex: 1 }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(address._id)}
                        className="btn btn-danger"
                        style={{ flex: 1 }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>

                  {/* Hover Accent */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle at bottom right, rgba(78, 205, 196, 0.1) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
